<template>
<v-responsive :aspect-ratio="16/9">
  <svg id="good3-svg-parangonHistory" height="100%" width="100%"></svg>
</v-responsive>
</template>

<script>
// D3.js for visualisations
import * as d3 from 'd3'

// MIXINS
import { ProfileLib } from '../../good3lib/profile_lib'

// VUEX
import { mapGetters } from 'vuex'

// DEFAULT
export default {
  name: 'GOOD3_SVG_HistoryParangon',

  // Importing VUEJS shared resources
  // • props for input
  // • components for sub vue components
  // • mixins for methods libraries
  props: {},
  components: {},
  mixins: [ProfileLib],

  // Accessible properties:
  // • data
  // • computed
  // • watch
  data: () => ({
    graphColors: {
      caption: '#bbbbbb',
      captionTitle: 'white'
    }
  }),
  computed: {
    // FROM VUEX
    ...mapGetters([
      'armorySelectedProfileHistory'
    ])
  },
  watch: {},

  // VUEJS lifecycle hooks
  //
  // • created
  // • mounted
  mounted () {
    if (this.armorySelectedProfileHistory !== null) {
      this.displayGraphParangonHistory(this.armorySelectedProfileHistory)
    }
  },

  // Components methods
  methods: {
    // initSVGGraph inits the graph with the base components and base properties
    initSVGGraph (pBaseDomID) {
      var vGood3Graph = {}

      // SVG root element
      vGood3Graph.svg = d3.select('#' + pBaseDomID)
      vGood3Graph.domID = pBaseDomID

      // margins
      vGood3Graph.margin = {
        top: 10,
        right: 10,
        bottom: 30,
        left: 40
      }

      // dimensions
      vGood3Graph.height = parseInt(vGood3Graph.svg.style('height'), 10)
      vGood3Graph.width = parseInt(vGood3Graph.svg.style('width'), 10)

      // ARTBOARD
      vGood3Graph.artboard = {
        height: vGood3Graph.height - vGood3Graph.margin.top - vGood3Graph.margin.bottom,
        width: vGood3Graph.width - vGood3Graph.margin.left - vGood3Graph.margin.right
      }

      vGood3Graph.artboard.g = vGood3Graph.svg.append('g')
        .attr('transform', 'translate(' + vGood3Graph.margin.left + ',' + vGood3Graph.margin.top + ')')

      // AXIS
      vGood3Graph.artboard.axis = {}

      // SCALES
      vGood3Graph.scale = {}

      // SERIES
      vGood3Graph.series = {}

      // FOCUS LINE
      vGood3Graph.focusline = {
        g: vGood3Graph.artboard.g.append('g')
          .attr('class', 'focus')
          .style('display', 'none')
      }
      vGood3Graph.focusline.line = vGood3Graph.focusline.g.append('line')
        .attr('id', vGood3Graph.domID + '-focusline')
        .attr('y1', vGood3Graph.artboard.height)
        .attr('y2', 0)
        .attr('pointer-events', 'none')

      // FOCUS POINTS
      vGood3Graph.focuspoints = {
        g: vGood3Graph.artboard.g.append('g')
          .style('display', 'none')
      }
      vGood3Graph.focuspoints.points = {}

      // TOOLTIP
      vGood3Graph.tooltip = {
        g: vGood3Graph.artboard.g.append('g')
          .attr('id', vGood3Graph.domID + '-tooltip')
          .attr('class', 'tooltip')
          .style('display', 'none')
      }

      // TOOLTIP CAPTIONS
      vGood3Graph.tooltip.captions = {}

      // TOUCHBOARD
      vGood3Graph.touchboard = {
        g: vGood3Graph.artboard.g.append('g')
          .append('rect')
          .attr('class', 'overlay')
          .attr('width', vGood3Graph.artboard.width)
          .attr('height', vGood3Graph.artboard.height)
      }

      // returning the object
      return vGood3Graph
    },

    // drawSerie uses the parameters inside the serie object to actually set
    // SVG path tag attributes, which draws the path on display.
    drawSerie (pSerie, pDataset) {
      pSerie.path.data([pDataset])
        .style('fill', 'none')
        .style('stroke', pSerie.color)
        .style('stroke-width', pSerie.width + 'px')
        .attr('d', pSerie.line)
        .attr('id', pSerie.domID)
    },

    // addAndDrawSerie attaches the serie to the SVG graph and draws it.
    addAndDrawSerie (pGood3Graph, pSerieID) {
      pGood3Graph.series[pSerieID] = {
        line: d3.line()
          .x(d => pGood3Graph.scale.x(Date.parse(d.audit_time)))
          .y(d => pGood3Graph.scale.y(d.bonus[pSerieID])),
        path: pGood3Graph.artboard.g.append('path'),
        color: pGood3Graph.scale.serie_colors(pSerieID),
        width: 2,
        domID: pGood3Graph.domID + '-series_' + pSerieID
      }
      this.drawSerie(pGood3Graph.series[pSerieID], pGood3Graph.dataset)
    },

    // addAndDrawFocusPoint attaches a new focus point
    addAndDrawFocusPoint (pGood3Graph, pSerieID) {
      pGood3Graph.focuspoints.points[pSerieID] = pGood3Graph.focuspoints.g.append('circle')
        .attr('id', pGood3Graph.domID + '-focuspoints_' + pSerieID)
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 5)
        .attr('fill', pGood3Graph.scale.serie_colors(pSerieID))
        // .attr('stroke', '#000000')
        .attr('pointer-events', 'none')
    },

    // addAndDrawCaptionTitle attaches a new caption to the tooltip
    addAndDrawCaptionTitle (pGood3Graph) {
      pGood3Graph.tooltip.captions.title = pGood3Graph.tooltip.g.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('font-family', 'Roboto')
        .attr('font-size', '11px')
        .attr('font-weight', '700')
        .attr('fill', this.graphColors.captionTitle)
    },

    // addAndDrawCaption attaches a new caption to the tooltip
    addAndDrawCaption (pGood3Graph, pSerieID, pRank) {
      pGood3Graph.tooltip.captions[pSerieID] = pGood3Graph.tooltip.g.append('text')
        .attr('x', 0)
        .attr('y', 20 * pRank)
        .attr('font-family', 'Roboto')
        .attr('font-size', '13px')
        .attr('font-weight', '500')
        .attr('fill', this.graphColors.caption)
    },

    // moveTooltip moves all the components of the tooltip
    moveTooltip (pGood3Graph, pX) {
      // if we are reaching the right border or the artboard we flip the caption
      // the caption the other side of the focusline which means the anchor has
      // to be set 'end' and not 'start'
      var anchorPosition = pX > (pGood3Graph.artboard.width - 120) ? 'end' : 'start'
      var padding = anchorPosition === 'end' ? -10 : 10

      // lets move now
      pGood3Graph.tooltip.g.attr('transform', 'translate(' + (pX + padding) + ',0)')

      for (var tCaption in pGood3Graph.tooltip.captions) {
        if (pGood3Graph.tooltip.captions.hasOwnProperty(tCaption)) {
          pGood3Graph.tooltip.captions[tCaption].attr('text-anchor', anchorPosition)
        }
      }
    },

    // updateTooltipCaptions updates the captions text in the tooltip
    updateTooltipCaptions (pGood3Graph, pTitleCaptionText, pSerieIDs, pCaptionTexts) {
      // updating the title
      pGood3Graph.tooltip.captions['title'].text(pTitleCaptionText)

      // updating the series
      for (var i = 0; i < pSerieIDs.length; i++) {
        pGood3Graph.tooltip.captions[pSerieIDs[i]].text(
          pGood3Graph.scale.serie_label(pSerieIDs[i]) +
          ': ' +
          pCaptionTexts[i]
        )
      }
    },

    // formatCaptionDate formats a javascript RFC date to something nicer for human
    // being
    formatCaptionDate (pDate) {
      return this.humanDate(pDate)
    },

    // moveFoculine moves all the components of the focusline including the dots
    moveFoculine (pGood3Graph, pX, pSerieIDs, pYCoordinates) {
      pGood3Graph.focusline.line.attr('transform', 'translate(' + pX + ',0)')

      for (var i = 0; i < pSerieIDs.length; i++) {
        pGood3Graph.focuspoints.points[pSerieIDs[i]].attr('cx', pX)
        pGood3Graph.focuspoints.points[pSerieIDs[i]].attr('cy', pYCoordinates[i])
      }
    },

    drawAxisX (pGood3Graph) {
      pGood3Graph.artboard.axis.x
        .attr('transform', 'translate(0,' + pGood3Graph.artboard.height + ')')
        .call(
          d3.axisBottom(pGood3Graph.scale.x)
            .ticks(d3.timeMonth.every(3))
            .tickFormat(d => d <= d3.timeYear(d) ? d.getFullYear() : null)
            .tickSize(0)
            .tickSizeInner(-pGood3Graph.artboard.height)
            .tickPadding(10)
        )
        .call(
          g => g.select('.domain')
            .remove()
        )
        .call(
          g => g.selectAll('.tick line')
            .attr('stroke-opacity', 0.4)
            .attr('stroke-dasharray', '1,4')
        )
    },

    drawAxisY (pGood3Graph) {
      pGood3Graph.artboard.axis.y
        .call(
          d3.axisLeft(pGood3Graph.scale.y)
            .ticks(10)
            .tickSize(0)
            .tickSizeInner(-pGood3Graph.artboard.width)
            .tickPadding(5)
        )
        .call(
          g => g.selectAll('.tick:not(:first-of-type) line')
            .attr('stroke-opacity', 0.4)
            .attr('stroke-dasharray', '1,4')
        )
    },

    // displayGraphParangonHistory uses D3.js to build a graph showing
    // the profile's parangon history since profile's creation.
    // pProfileHistory is []*good3ObjectProfileHistory
    //
    // This procedure follows GOOD3 SVG graph standard which we detail here.
    // All GOOD3 SVG graphs are defined with the following components:
    //
    // • artboard: this is where we draw the graph, this does not include
    //   legends nor does it include ticks. this is a SVG group.
    // • scale: this is where we store the scales to draw the graphs.
    // • series: this is the object storing the series of graphs.
    // • focusline: the focus line is following the mouse cursor to provide
    //   values of the graphs.
    // • touchboard: the transparent canvas catching the mouse position & moves.
    // • tooltip: the canvas used to display values where the focus line crosses
    //   the series graphs.
    //
    // an object called good3Graph will group all these components.
    //
    displayGraphParangonHistory (pDataset) {
      // data sanity check to avoid doing much with no data
      if (pDataset === null) {
        alert('no way to draw anything without a valid dataset!')
      }

      // INITIALISE: creating a the new good3 graph
      var good3Graph = this.initSVGGraph('good3-svg-parangonHistory')
      good3Graph.dataset = pDataset

      // SCALES: definition
      good3Graph.scale.x = d3.scaleTime()
        .domain(d3.extent(pDataset, d => Date.parse(d.audit_time)))
        .range([0, good3Graph.artboard.width])
      good3Graph.scale.y = d3.scaleLinear()
        .domain([0, d3.max(pDataset, d => +d.bonus.total_parangon) * 1.05])
        .range([good3Graph.artboard.height, 0])
      good3Graph.scale.serie_colors = d3.scaleOrdinal()
        .domain(['total_parangon_sc', 'total_parangon_hc', 'total_parangon'])
        .range(['silver', 'red', 'steelblue'])
      good3Graph.scale.serie_label = d3.scaleOrdinal()
        .domain(['total_parangon_sc', 'total_parangon_hc', 'total_parangon'])
        .range(['normal', 'hardcore', 'total'])

      // displaying the scales (bottom and left)
      good3Graph.artboard.axis.x = good3Graph.artboard.g.append('g')
      this.drawAxisX(good3Graph)
      good3Graph.artboard.axis.y = good3Graph.artboard.g.append('g')
      this.drawAxisY(good3Graph)

      // SERIES: adding the 3 series
      this.addAndDrawSerie(good3Graph, 'total_parangon_sc')
      this.addAndDrawSerie(good3Graph, 'total_parangon_hc')
      this.addAndDrawSerie(good3Graph, 'total_parangon')

      // FOCUS POINTS: adding focus points to each serie
      this.addAndDrawFocusPoint(good3Graph, 'total_parangon_sc')
      this.addAndDrawFocusPoint(good3Graph, 'total_parangon_hc')
      this.addAndDrawFocusPoint(good3Graph, 'total_parangon')

      // TOOLTIP: adding the tooltip content
      this.addAndDrawCaptionTitle(good3Graph)
      this.addAndDrawCaption(good3Graph, 'total_parangon', 1)
      this.addAndDrawCaption(good3Graph, 'total_parangon_sc', 2)
      this.addAndDrawCaption(good3Graph, 'total_parangon_hc', 3)

      // MOUSE MOVMENT TRACKING
      // since the event handler function will make use of methods defined within
      // the vue component's method context, we need to pass this context to the
      // event handler function defined as a => function (with no access to 'this')
      var vVueJSContext = this

      // mouse movement handling function
      function mousemoveSVG () {
        // function which will be used to get the abscissa from the mouse x coordinate
        // and link the closest 'left' data point to it.
        var bisectDate = d3.bisector(d => Date.parse(d.audit_time)).left
        var x0 = good3Graph.scale.x.invert(d3.mouse(this)[0])
        var d = pDataset[bisectDate(pDataset, x0, 1)]

        // the actual abscissa of the focusline and points
        var xr = good3Graph.scale.x(Date.parse(d.audit_time))
        var yP = good3Graph.scale.y(d.bonus.total_parangon)
        var yPsc = good3Graph.scale.y(d.bonus.total_parangon_sc)
        var yPhc = good3Graph.scale.y(d.bonus.total_parangon_hc)

        // lets move guys!
        vVueJSContext.moveFoculine(good3Graph, xr,
          ['total_parangon', 'total_parangon_sc', 'total_parangon_hc'],
          [yP, yPsc, yPhc]
        )
        vVueJSContext.moveTooltip(good3Graph, xr)
        vVueJSContext.updateTooltipCaptions(good3Graph, vVueJSContext.formatCaptionDate(d.audit_time),
          ['total_parangon', 'total_parangon_sc', 'total_parangon_hc'],
          [d.bonus.total_parangon, d.bonus.total_parangon_sc, d.bonus.total_parangon_hc]
        )
      }

      good3Graph.touchboard.g.on('mouseover', function () {
        good3Graph.focusline.g.style('display', null)
        good3Graph.focuspoints.g.style('display', null)
        good3Graph.tooltip.g.style('display', null)
      }).on('mouseout', function () {
        good3Graph.focusline.g.style('display', 'none')
        good3Graph.focuspoints.g.style('display', 'none')
        good3Graph.tooltip.g.style('display', 'none')
      }).on('mousemove', mousemoveSVG)

      // Define responsive behavior
      function resize () {
        // BASE SIZE
        good3Graph.height = parseInt(good3Graph.svg.style('height'), 10)
        good3Graph.width = parseInt(good3Graph.svg.style('width'), 10)

        // ARTBOARD
        good3Graph.artboard.height = good3Graph.height - good3Graph.margin.top - good3Graph.margin.bottom
        good3Graph.artboard.width = good3Graph.width - good3Graph.margin.left - good3Graph.margin.right

        // SCALES
        good3Graph.scale.x.range([0, good3Graph.artboard.width])
        good3Graph.scale.y.range([good3Graph.artboard.height, 0])

        // AXIS
        vVueJSContext.drawAxisX(good3Graph)
        vVueJSContext.drawAxisY(good3Graph)

        // SERIES: redrawing the 3 series
        good3Graph.series.total_parangon_sc.line = d3.line()
          .x(d => good3Graph.scale.x(Date.parse(d.audit_time)))
          .y(d => good3Graph.scale.y(d.bonus.total_parangon_sc))
        vVueJSContext.drawSerie(good3Graph.series.total_parangon_sc, good3Graph.dataset)
        good3Graph.series.total_parangon_hc.line = d3.line()
          .x(d => good3Graph.scale.x(Date.parse(d.audit_time)))
          .y(d => good3Graph.scale.y(d.bonus.total_parangon_hc))
        vVueJSContext.drawSerie(good3Graph.series.total_parangon_hc, good3Graph.dataset)
        good3Graph.series.total_parangon.line = d3.line()
          .x(d => good3Graph.scale.x(Date.parse(d.audit_time)))
          .y(d => good3Graph.scale.y(d.bonus.total_parangon))
        vVueJSContext.drawSerie(good3Graph.series.total_parangon, good3Graph.dataset)

        // FOCUSLINE
        good3Graph.focusline.line
          .attr('y1', good3Graph.artboard.height)
          .attr('y2', 0)
          .attr('pointer-events', 'none')

        // TOUCHBOARD
        good3Graph.touchboard.g
          .attr('width', good3Graph.artboard.width)
          .attr('height', good3Graph.artboard.height)
      }

      // RESPONSIVNESS
      window.addEventListener('resize', resize)
    }
  }
}
</script>

<style scoped>

</style>
